import React, { memo, useEffect, useState } from 'react';

import { connect, useDispatch } from 'react-redux';
import mova from 'mova';
import './DishDetailsModal.scss';
import Modal from '@components/feedback/Modal/Modal';
import SvgIcon from '@components/display/SvgIcon/SvgIcon';
// import Checkbox from 'view/components/inputs/Checkbox/Checkbox';
// import TextArea from 'view/components/inputs/TextArea/TextArea';
import { closeModal } from '@store/actions/modal';
import { addNewDish, placeOrder, toggleLikeDish } from '@store/actions/menu';
import Form from 'view/components/inputs/Form/Form';
import currency from '@filters/currency';
import Button from 'view/components/inputs/Button/Button';
import DishHeader from './DishHeader';
import DishOptions from './DishOptions';
import DishQuantitySelector from './DishQuantitySelector';

const t = mova.ns('containers.DishDetailsModal');

const DishDetailsModal = ({ meta }) => {
  const dispatch = useDispatch();
  const [quantity, setQuantity] = useState(1);
  // const [addComment, setAddComment] = useState(false);
  const [formData, setFormData] = useState({});
  const dish = meta;
  const { dishOptionCategories } = dish || {};
  useEffect(() => {
    const initialData = {};
    dishOptionCategories?.forEach((cat) => {
      const catKey = `optionCatId-${cat.id}`;

      // Priority 1: Quantity selector (maxSameOptionSelection > 1)
      if (cat.variables?.maxSameOptionSelection > 1) {
        const quantityValues = {};
        cat.dishOptions.forEach((op) => {
          quantityValues[op.id] = 0;
        });
        initialData[catKey] = quantityValues;
      }
      // Priority 2: Checkbox (maxSelection > 1)
      else if (cat.variables?.maxSelection > 1) {
        const checkboxValues = {};
        cat.dishOptions.forEach((op) => {
          checkboxValues[op.id] = false;
        });
        initialData[catKey] = checkboxValues;
      }
      // Priority 3: Radio button (minSelection === 1)
      else if (cat.variables?.minSelection === 1) {
        const defaultOptionId = cat.dishOptions[0]?.id;
        if (defaultOptionId) {
          initialData[catKey] = String(defaultOptionId);
        }
      }
    });
    setFormData(initialData);
  }, [dishOptionCategories]);

  const calculateTotalPrice = () => {
    let total = dish.price;

    Object.entries(formData).forEach(([key, value]) => {
      if (key.startsWith('optionCatId-')) {
        const catId = key.split('-')[1];
        const optionCategory = dishOptionCategories.find((cat) => String(cat.id) === catId);

        if (!optionCategory) return;

        if (typeof value === 'string') {
          const option = optionCategory.dishOptions.find((op) => op.id === Number(value));
          if (option) total += option.price;
        }

        if (typeof value === 'object') {
          Object.entries(value).forEach(([optionId, amountOrChecked]) => {
            const option = optionCategory.dishOptions.find((op) => op.id === Number(optionId));
            if (!option) return;

            if (typeof amountOrChecked === 'boolean' && amountOrChecked) {
              total += option.price;
            }

            if (typeof amountOrChecked === 'number' && amountOrChecked > 0) {
              total += option.price * amountOrChecked;
            }
          });
        }
      }
    });
    return total * quantity;
  };

  const handleOrder = () => {
    // const comment = formData.comment || '';
    const structuredOptions = {};

    Object.entries(formData).forEach(([key, value]) => {
      if (key.startsWith('optionCatId-')) {
        const catId = key.split('-')[1];

        if (typeof value === 'string') {
          structuredOptions[catId] = [Number(value)];
        } else if (typeof value === 'object') {
          const selectedOptions = Object.entries(value).flatMap(([optionId, countOrChecked]) => {
            if (typeof countOrChecked === 'boolean' && countOrChecked) {
              return [Number(optionId)];
            }
            if (typeof countOrChecked === 'number' && countOrChecked > 0) {
              return Array(countOrChecked).fill(Number(optionId));
            }
            return [];
          });

          if (selectedOptions.length) {
            structuredOptions[catId] = selectedOptions;
          }
        }
      }
    });

    const optionCountsMap = {};

    Object.values(structuredOptions).flat().forEach((optionId) => {
      if (!optionCountsMap[optionId]) {
        optionCountsMap[optionId] = 0;
      }
      optionCountsMap[optionId]++;
    });

    const options = Object.entries(optionCountsMap).map(([optionId, count]) => ({
      count,
      option: { id: Number(optionId) },
    }));

    const dishToAdd = {
      dish: { id: dish.id },
      count: quantity,
      // comment: comment || null,
      options,
    };

    dispatch(addNewDish(dishToAdd));
    dispatch(closeModal());
  };

  const handleCloseModal = () => {
    dispatch(closeModal());
  };

  return (
    <Modal className={`dish-details-modal ${dish.photoUrl ? 'dish-details-modal--photo' : ''}`}>
      <div className='dish-details-modal__close-button-wrapper'>
        <Button inputClass='dish-details-modal__close-button' onClick={handleCloseModal}>
          <SvgIcon className='dish-details-modal__cancel-icon' type='cancel' fill='#fff' color='#000' />
        </Button>
      </div>

      <div className='dish-details-modal__wrapper'>
        {dish.photoUrl && (
          <div className='dish-details-modal__img-column'>
            {dish.photoUrl && <img className='dish-details-modal__photo' src={dish.photoUrl} alt={dish.name} />}
          </div>
        )}

        <div className='dish-details-modal__info-column'>
          <DishHeader dish={dish} />
          <Form
            onValidSubmit={handleOrder}
            onChange={(currentFormData) => {
              setFormData((prev) => ({
                ...prev,
                ...currentFormData,
              }));
            }}
            className='dish-details-modal__form'
          >
            <DishOptions dishOptionCategories={dishOptionCategories} formData={formData} setFormData={setFormData} />
            {/* <div className='dish-details-modal__checkbox'>
                            <Checkbox name='addComment' onClick={() => setAddComment(!addComment)}>
                                {t('addComment')}
                            </Checkbox>
                        </div>
                        {addComment && (
                            <TextArea className='dish-details-modal__comment' name='comment' placeholder={t('comment')} fullWidth />
                        )} */}
            <DishQuantitySelector quantity={quantity} setQuantity={setQuantity} style={{ marginTop: '16px' }} />
            <Button inputClass='dish-details-modal__submit-button' fullWidth behavior='submit'>
              {t('confirm')} {currency(calculateTotalPrice())}
            </Button>
          </Form>
        </div>
      </div>
    </Modal>
  );
};

const mapStateToProps = ({ modal }) => ({
  meta: modal.params,
});

const mapDispatchToProps = {
  toggleLikeDish,
  placeOrder,
};

export default memo(connect(mapStateToProps, mapDispatchToProps)(DishDetailsModal));
